import React from 'react';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  Scale,
  ShieldCheck,
  FileText,
  ClipboardCheck,
  Lock,
  CheckCircle2,
  ArrowUpRight,
  Sparkles,
  BookOpen,
  Users,
  AlertCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SafeJsonLd from '@/components/SafeJsonLd';
import { useLanguage } from '@/contexts/LanguageContext';
import ServicePricing, { PricingTier } from '@/components/service/ServicePricing';
import ServiceFAQ, { FAQItem } from '@/components/service/ServiceFAQ';
import ServiceCrossSell from '@/components/service/ServiceCrossSell';
import ServiceBreadcrumb from '@/components/service/ServiceBreadcrumb';

const ServicesDpo: React.FC = () => {
  const { t, language } = useLanguage();
  const isPT = language === 'pt';

  const features = [
    { Icon: ShieldCheck,    key: 'designated' },
    { Icon: ClipboardCheck, key: 'audits' },
    { Icon: BookOpen,       key: 'training' },
  ] as const;

  const featureCopy = isPT ? {
    designated: {
      title: 'DPO Externo designado',
      desc: 'Nomeamos o Encarregado de Proteção de Dados em nome da sua empresa. Comunicação à CNPD, contacto público e ponto único de resposta a titulares.',
    },
    audits: {
      title: 'Auditorias e DPIA',
      desc: 'Mapeamos os tratamentos, identificamos riscos e produzimos Avaliações de Impacto (DPIA) onde a lei obriga. Plano de remediação com prazos claros.',
    },
    training: {
      title: 'Formação & cultura RGPD',
      desc: 'Sessões para a equipa em linguagem simples. Procedimentos internos, modelos de resposta a titulares e checklist de violações de dados.',
    },
  } : {
    designated: {
      title: 'Designated External DPO',
      desc: 'We designate the Data Protection Officer on behalf of your company. CNPD filing, public point of contact and single point of response for data subjects.',
    },
    audits: {
      title: 'Audits and DPIAs',
      desc: 'We map your processing activities, identify risks and produce Data Protection Impact Assessments where required. Remediation plan with clear deadlines.',
    },
    training: {
      title: 'GDPR training & culture',
      desc: 'Plain-language sessions for the team. Internal procedures, data-subject response templates and a breach-response checklist.',
    },
  };

  const title = isPT
    ? 'DPO Externo (Encarregado de Proteção de Dados) | Spawncore'
    : 'External DPO (Data Protection Officer) | Spawncore';

  const description = isPT
    ? 'Serviço de DPO externo (Encarregado de Proteção de Dados) para pequenas e médias empresas em Portugal. Conformidade com o RGPD, Lei n.º 58/2019, auditorias, DPIA, formação e resposta a titulares.'
    : 'External DPO (Data Protection Officer) service for small and mid-sized businesses in Portugal. GDPR + Portuguese Law 58/2019 compliance, audits, DPIA, training and data-subject response.';

  const tiers: PricingTier[] = isPT ? [
    {
      name: 'Diagnóstico',
      price: 'Sob proposta',
      description: 'Avaliação inicial pontual: onde está e o que falta fazer.',
      features: [
        'Inventário de tratamentos de dados',
        'Análise de risco e gap RGPD',
        'Avaliação da política de privacidade atual',
        'Relatório executivo com prioridades',
        'Plano de ação a 90 dias',
        'Sessão de apresentação à direção',
      ],
      ctaLabel: 'Pedir diagnóstico',
    },
    {
      name: 'DPO Mensal',
      price: 'Sob proposta',
      description: 'Encarregado externo designado, com apoio contínuo.',
      features: [
        'DPO designado e comunicado à CNPD',
        'Ponto de contacto público para titulares',
        'Atendimento e resposta a pedidos de titulares',
        'Atualização do registo de tratamentos',
        'Apoio em violações de dados (breach response)',
        'Relatório trimestral à gerência',
        'Bolsa de horas para consultas',
      ],
      popular: true,
      ctaLabel: 'Escolher DPO Mensal',
    },
    {
      name: 'Premium',
      price: 'Sob proposta',
      description: 'Para operações complexas ou setores regulados.',
      features: [
        'Tudo do plano DPO Mensal',
        'Auditoria presencial anual',
        'DPIA para novos projetos (até 4/ano)',
        'Formação anual à equipa (até 20 pessoas)',
        'Revisão de contratos com subcontratantes',
        'Resposta prioritária a incidentes (24h)',
        'Reuniões trimestrais de comité',
      ],
      ctaLabel: 'Falar com o estúdio',
    },
  ] : [
    {
      name: 'Diagnostic',
      price: 'On request',
      description: 'One-off initial assessment: where you stand and what is missing.',
      features: [
        'Inventory of data-processing activities',
        'GDPR gap and risk analysis',
        'Review of current privacy policy',
        'Executive report with priorities',
        '90-day action plan',
        'Briefing session with leadership',
      ],
      ctaLabel: 'Request the diagnostic',
    },
    {
      name: 'Monthly DPO',
      price: 'On request',
      description: 'Designated external DPO with ongoing support.',
      features: [
        'Designated DPO, filed with the CNPD',
        'Public point of contact for data subjects',
        'Handling of data-subject requests',
        'Records of processing kept up to date',
        'Breach response support',
        'Quarterly report to management',
        'Hour pool for consultations',
      ],
      popular: true,
      ctaLabel: 'Choose Monthly DPO',
    },
    {
      name: 'Premium',
      price: 'On request',
      description: 'For complex operations or regulated sectors.',
      features: [
        'Everything in Monthly DPO',
        'Annual on-site audit',
        'DPIA for new projects (up to 4/year)',
        'Annual team training (up to 20 people)',
        'Processor contract reviews',
        'Priority incident response (24h)',
        'Quarterly committee meetings',
      ],
      ctaLabel: 'Talk to the studio',
    },
  ];

  const faqs: FAQItem[] = isPT ? [
    {
      question: 'Sou obrigado a ter DPO?',
      answer: 'A designação de DPO é obrigatória quando trata dados pessoais em larga escala, faz monitorização sistemática (ex: vídeo-vigilância intensiva, perfis de clientes), ou trata categorias especiais (saúde, biometria, dados sensíveis). Mesmo quando não é obrigatório, ter um DPO externo é a forma mais eficiente de garantir conformidade sem contratar internamente.',
    },
    {
      question: 'O que faz exatamente um DPO externo?',
      answer: 'Aconselha sobre obrigações RGPD, monitoriza a conformidade interna, é ponto de contacto com a CNPD e com os titulares de dados, avalia DPIAs, apoia em violações de dados e dá formação à equipa. Mantém-se independente da gestão operacional — esse é exatamente o ponto.',
    },
    {
      question: 'Quanto custa?',
      answer: 'Depende do volume de tratamentos, número de colaboradores, setor (saúde e financeiro pesam mais) e se há sistemas legados a auditar. Após uma chamada de 30 minutos sem custo apresentamos proposta firme. Tipicamente, PMEs em Portugal investem entre €150 e €600/mês em DPO externo — sem o custo de contratar internamente.',
    },
    {
      question: 'E se houver uma violação de dados (data breach)?',
      answer: 'Tem 72 horas para notificar a CNPD após tomar conhecimento. Apoiamos na investigação, no envio do formulário oficial e na comunicação aos titulares quando há risco elevado. No plano Premium garantimos resposta em 24h — críticos não esperam por horário comercial.',
    },
    {
      question: 'Conseguem ajudar com a CNPD ou um pedido de informação?',
      answer: 'Sim. Acompanhamos pedidos de informação, processos de fiscalização e respostas a coimas. Como DPO designado, somos o interlocutor oficial. Em casos com risco de litígio coordenamos com o seu advogado externo.',
    },
    {
      question: 'Trabalham com setores regulados (saúde, financeiro)?',
      answer: 'Sim. Trabalhamos com clínicas, consultórios, gabinetes de contabilidade e advogados, mediadores de seguros e e-commerce com dados de cartão. Cada setor tem orientações específicas da CNPD que aplicamos.',
    },
  ] : [
    {
      question: 'Am I required to appoint a DPO?',
      answer: 'A DPO is required when you carry out large-scale processing, systematic monitoring (e.g. intensive video surveillance, customer profiling), or process special categories of data (health, biometric, sensitive). Even when not strictly required, an external DPO is the most efficient way to stay compliant without hiring in-house.',
    },
    {
      question: 'What does an external DPO actually do?',
      answer: 'Advises on GDPR obligations, monitors internal compliance, acts as point of contact with the CNPD and with data subjects, reviews DPIAs, supports breach handling and trains the team. Independent from operational management by design.',
    },
    {
      question: 'How much does it cost?',
      answer: 'It depends on processing volume, headcount, sector (healthcare and finance weigh more) and whether legacy systems need auditing. After a free 30-minute call we send a firm proposal. Typically SMEs in Portugal invest between €150 and €600/month for an external DPO — without the cost of an in-house hire.',
    },
    {
      question: 'What if there is a data breach?',
      answer: 'You have 72 hours to notify the CNPD after becoming aware. We support investigation, official filing and data-subject notification when risk is high. On the Premium plan we guarantee 24h response — critical incidents do not wait for business hours.',
    },
    {
      question: 'Can you help with the CNPD or an information request?',
      answer: 'Yes. We handle information requests, supervisory inspections and fine responses. As designated DPO we are the official interlocutor. Where litigation risk is present we coordinate with your external lawyer.',
    },
    {
      question: 'Do you work with regulated sectors (healthcare, finance)?',
      answer: 'Yes. We work with clinics, medical offices, accounting and law firms, insurance brokers and e-commerce handling card data. Each sector has specific CNPD guidance we apply.',
    },
  ];

  const benefits = isPT ? [
    'Designação formal de DPO comunicada à CNPD',
    'Inventário e registo de tratamentos sempre atualizado',
    'Política de privacidade, cookies e RGPD revistas',
    'Procedimentos de resposta a titulares de dados',
    'Modelos de consentimento legalmente válidos',
    'Procedimento interno de violação de dados (72h)',
    'Cláusulas RGPD para contratos com subcontratantes',
    'Formação anual obrigatória à equipa',
  ] : [
    'Formal DPO designation filed with the CNPD',
    'Always up-to-date inventory and records of processing',
    'Privacy, cookie and GDPR policies reviewed',
    'Data-subject response procedures',
    'Legally valid consent templates',
    'Internal 72h data breach procedure',
    'GDPR clauses for processor contracts',
    'Mandatory annual training for the team',
  ];

  const useCases = isPT ? [
    { title: 'Clínicas & saúde',     desc: 'Categorias especiais de dados, retenção de fichas clínicas, partilha com seguradoras e laboratórios.' },
    { title: 'Contabilistas & advogados', desc: 'Dados de clientes, sigilo profissional vs. RGPD, retenção a 10 anos e arquivo seguro.' },
    { title: 'E-commerce & retalho', desc: 'Carrinho, pagamentos, cookies, retargeting, dados de cliente e tratamento por subcontratantes.' },
    { title: 'Imobiliárias',         desc: 'Captação de leads, KYC de inquilinos, vídeo-vigilância em imóveis e gestão de visitas.' },
    { title: 'RH & recrutamento',    desc: 'Candidaturas, base de talento, conservação de CVs e testes psicotécnicos.' },
    { title: 'Serviços B2B',         desc: 'Outreach a contactos profissionais, CRM, propostas e marketing por email.' },
  ] : [
    { title: 'Clinics & healthcare',     desc: 'Special-category data, medical-record retention, sharing with insurers and labs.' },
    { title: 'Accountants & lawyers',    desc: 'Client data, professional secrecy vs. GDPR, 10-year retention and secure archiving.' },
    { title: 'E-commerce & retail',      desc: 'Cart, payments, cookies, retargeting, customer data and processor handling.' },
    { title: 'Real estate',              desc: 'Lead capture, tenant KYC, video surveillance and visit management.' },
    { title: 'HR & recruitment',         desc: 'Applications, talent pool, CV retention and psychometric testing.' },
    { title: 'B2B services',             desc: 'Outreach to professional contacts, CRM, proposals and email marketing.' },
  ];

  const frameworks = [
    { Icon: Scale,          name: 'RGPD / GDPR',            desc: isPT ? 'Regulamento (UE) 2016/679' : 'Regulation (EU) 2016/679' },
    { Icon: FileText,       name: 'Lei n.º 58/2019',         desc: isPT ? 'Execução do RGPD em Portugal' : 'Portuguese implementation of GDPR' },
    { Icon: ShieldCheck,    name: 'Orientações CNPD',        desc: isPT ? 'Deliberações e guias da CNPD' : 'CNPD deliberations and guides' },
    { Icon: Lock,           name: 'ISO 27001 / 27701',       desc: isPT ? 'Segurança e privacidade'      : 'Security and privacy' },
    { Icon: BookOpen,       name: 'EDPB Guidelines',         desc: isPT ? 'Conselho Europeu de Proteção' : 'European Data Protection Board' },
    { Icon: AlertCircle,    name: 'NIS2 / DORA',             desc: isPT ? 'Quando aplicável ao setor'    : 'Where applicable to the sector' },
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Data Protection Officer (DPO) Services',
    name: isPT ? 'DPO Externo (Encarregado de Proteção de Dados)' : 'External Data Protection Officer (DPO)',
    description,
    provider: { '@type': 'Organization', name: 'Spawncore', url: 'https://spawncore.com' },
    areaServed: 'PT',
    audience: { '@type': 'BusinessAudience', audienceType: 'Small and Mid-sized Business' },
    url: 'https://spawncore.com/services/dpo',
    offers: tiers.map(t => ({
      '@type': 'Offer',
      name: t.name,
      priceCurrency: 'EUR',
      description: t.description,
    })),
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={isPT ? 'pt' : 'en'} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={isPT
          ? 'DPO externo Portugal, encarregado proteção dados, RGPD Portugal, GDPR PME, conformidade RGPD, CNPD, DPIA, Lei 58/2019, proteção dados pessoais, DPO outsourcing'
          : 'external DPO Portugal, data protection officer, GDPR Portugal, GDPR SME, GDPR compliance, CNPD, DPIA, Law 58/2019, personal data protection, DPO outsourcing'} />
        <link rel="canonical" href="https://spawncore.com/services/dpo" />
        <link rel="alternate" hrefLang="pt-PT" href="https://spawncore.com/services/dpo" />
        <link rel="alternate" hrefLang="en" href="https://spawncore.com/services/dpo" />
        <link rel="alternate" hrefLang="x-default" href="https://spawncore.com/services/dpo" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spawncore.com/services/dpo" />
        <meta property="og:image" content="https://spawncore.com/lovable-uploads/c121560e-1be0-4632-8106-cdadaf79cf04.png" />
        <meta property="og:locale" content={isPT ? 'pt_PT' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>

      <SafeJsonLd data={serviceSchema} />

      <header>
        <Navigation />
      </header>

      <ServiceBreadcrumb label={isPT ? 'DPO Externo' : 'External DPO'} url="https://spawncore.com/services/dpo" />

      <main>
        {/* Hero */}
        <section className="relative pt-12 pb-20 md:pt-20 md:pb-28 overflow-hidden">
          <div className="pointer-events-none absolute inset-0 mesh-bg opacity-60" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <span className="pill-tag mb-8">
              <Scale className="h-3 w-3" />
              {isPT ? 'DPO Externo · Conformidade RGPD' : 'External DPO · GDPR Compliance'}
            </span>
            <h1 className="font-display text-foreground text-[clamp(2.5rem,7vw,5.5rem)] leading-[0.96] tracking-tight">
              {isPT ? 'Conformidade ' : 'GDPR compliance '}
              <span className="italic font-display text-primary">
                {isPT ? 'RGPD' : 'made'}
              </span>
              {isPT ? ' sem dor de cabeça.' : ' painless.'}
            </h1>
            <p className="mt-7 text-lg md:text-2xl text-muted-foreground max-w-2xl leading-relaxed">
              {isPT
                ? 'DPO externo para pequenas e médias empresas em Portugal. Sem contratar internamente. Sem coimas evitáveis.'
                : 'External DPO for small and mid-sized businesses in Portugal. No in-house hire. No avoidable fines.'}
            </p>
            <p className="mt-5 text-base text-muted-foreground/80 max-w-2xl leading-relaxed">
              {isPT
                ? 'Designamos o Encarregado de Proteção de Dados, comunicamos à CNPD, mantemos o registo de tratamentos atualizado, respondemos a titulares e formamos a sua equipa. Tudo isto por uma fração do custo de uma contratação interna.'
                : 'We designate the Data Protection Officer, file with the CNPD, keep your records of processing up to date, handle data-subject requests and train your team — for a fraction of the cost of an in-house hire.'}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="group h-14 px-7 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
                >
                  <Scale className="mr-2 h-4 w-4" />
                  {isPT ? 'Marcar diagnóstico' : 'Book the diagnostic'}
                  <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <a href="#pricing" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-7 rounded-full bg-card/40 border-border hover:bg-card text-base w-full sm:w-auto"
                >
                  {isPT ? 'Ver planos' : 'See plans'}
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {isPT ? 'O que faz um DPO externo' : 'What an external DPO does'}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Três pilares de conformidade.' : 'Three pillars of compliance.'}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {features.map(({ Icon, key }, idx) => (
                <article
                  key={key}
                  className="group relative overflow-hidden rounded-2xl border border-border/80 bg-card/70 backdrop-blur-sm p-8 transition-all duration-500 hover:bg-card hover:border-primary/40 hover:-translate-y-1"
                >
                  <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-primary/[0.10] blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-secondary text-primary">
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </div>
                      <span className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
                        / 0{idx + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-[1.625rem] leading-tight tracking-tight mb-3 text-foreground">
                      {featureCopy[key].title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {featureCopy[key].desc}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Use cases by industry */}
        <section className="py-20 md:py-28 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-12 gap-6 mb-14">
              <div className="col-span-12 md:col-span-7">
                <span className="eyebrow">— {isPT ? 'Por setor' : 'By sector'}</span>
                <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                  {isPT ? 'Setores que apoiamos com DPO externo.' : 'Sectors we support with an external DPO.'}
                </h2>
              </div>
              <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-muted-foreground text-base md:text-lg leading-relaxed">
                {isPT
                  ? 'Cada setor tem orientações específicas da CNPD. Aplicamos as que se aplicam ao seu.'
                  : 'Each sector has specific CNPD guidance. We apply what applies to yours.'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {useCases.map((uc, idx) => (
                <article
                  key={uc.title}
                  className="group rounded-xl border border-border/80 bg-card/60 p-6 hover:bg-card hover:border-primary/30 transition-colors"
                >
                  <div className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70 mb-3">
                    / 0{idx + 1}
                  </div>
                  <h3 className="font-display text-xl text-foreground mb-2.5 leading-tight">{uc.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{uc.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits / Included */}
        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border/60">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mb-14 max-w-3xl">
              <span className="eyebrow">— {isPT ? 'O que está incluído' : "What's included"}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Os entregáveis que ficam consigo.' : 'The deliverables that stay with you.'}
              </h2>
            </div>
            <ul className="grid md:grid-cols-2 gap-x-12 gap-y-5">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-4 group">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/[0.10] text-primary group-hover:bg-primary/[0.20] transition-colors">
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <span className="text-foreground/90 text-base md:text-lg leading-relaxed pt-0.5">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Frameworks & Standards */}
        <section className="py-20 md:py-24 bg-background">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 max-w-3xl">
              <span className="eyebrow">— {isPT ? 'Quadro legal' : 'Legal frameworks'}</span>
              <h2 className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight">
                {isPT ? 'Standards e regulamentos que aplicamos.' : 'Standards and regulations we apply.'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-border/60 rounded-2xl overflow-hidden border border-border/60">
              {frameworks.map(({ Icon, name, desc }) => (
                <div key={name} className="bg-card/60 p-5 hover:bg-card transition-colors">
                  <Icon className="h-5 w-5 text-primary mb-3" strokeWidth={1.6} />
                  <div className="font-display text-sm text-foreground leading-tight">{name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <div id="pricing">
          <ServicePricing
            eyebrow={isPT ? 'Planos' : 'Plans'}
            heading={isPT ? 'Três formatos. Sempre sob proposta.' : 'Three formats. Always on request.'}
            subtitle={isPT
              ? 'Cada empresa é diferente: volume de tratamentos, setor, sistemas legados. Fazemos uma chamada gratuita e apresentamos proposta firme em 48h.'
              : 'Every company is different: processing volume, sector, legacy systems. We do a free call and send a firm proposal within 48h.'}
            tiers={tiers}
            note={isPT
              ? 'Sem fidelizações longas. Cancele com 30 dias. Documentação fica sempre em seu nome.'
              : 'No long lock-ins. Cancel with 30 days notice. Documentation always remains in your name.'}
          />
        </div>

        {/* FAQ */}
        <ServiceFAQ
          eyebrow={isPT ? 'Perguntas frequentes · DPO' : 'FAQ · DPO'}
          heading={isPT ? 'O que costumam perguntar sobre DPO externo.' : 'What people ask about external DPO.'}
          subtitle={isPT ? 'Respostas claras, sem juridiquês.' : 'Clear answers, no legalese.'}
          faqs={faqs}
        />

        {/* Final CTA */}
        <section className="py-20 md:py-28 bg-background relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0 mesh-bg opacity-60" aria-hidden="true" />
          <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <span className="pill-tag mb-6">
              <Sparkles className="h-3 w-3" />
              {isPT ? 'Chamada de 30 min · grátis' : 'Free 30-min call'}
            </span>
            <h2 className="font-display text-foreground text-[clamp(2rem,5vw,4rem)] leading-[1.02] tracking-tight mb-6">
              {isPT ? 'Pronto para dormir descansado?' : 'Ready to sleep better at night?'}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              {isPT
                ? 'Marque uma chamada gratuita. Avaliamos o seu nível de risco RGPD e mostramos o que muda no primeiro mês.'
                : 'Book a free call. We assess your GDPR risk level and show what changes in the first month.'}
            </p>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="group h-14 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary-light shadow-glow border border-primary-light/30 text-base w-full sm:w-auto"
              >
                <Users className="mr-2 h-4 w-4" />
                {isPT ? 'Falar com o estúdio' : 'Talk to the studio'}
                <ArrowUpRight className="ml-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </section>

        <ServiceCrossSell current="dpo" />
      </main>

      <Footer />
    </div>
  );
};

export default ServicesDpo;
